/* The agent header's personalization bar: label + hint + level word over a
 * three-segment meter, one segment per level. Presentational — the caller
 * resolves the level (see usePersonalizationSignals) and gates on the flag. */

import { useLocation } from "react-router-dom";

import { FLAGS_ENABLED } from "../../services/flags";
import { MetricHint } from "../overview/KpiCards";
import {
  PERSONALIZATION_CAPTION,
  PERSONALIZATION_HINT,
  PERSONALIZATION_LABEL,
  levelIndex,
  type PersonalizationLevel,
} from "./personalizationThresholds";
import { usePersonalizationSignals } from "./usePersonalizationSignals";

const SEGMENTS = 3;

/* Filled-segment colour per level. The chart `--mark-*` inks (not the darker
 * `--val-*` text inks) — these are the meter's own marks, and read at 3px. */
const FILL: Record<PersonalizationLevel, string> = {
  low: "var(--label)",
  medium: "var(--mark-amber)",
  high: "var(--mark-green)",
};

/** Preview escape hatch: `?plevel=medium` forces a level so all three states
 *  can be reviewed without manufacturing traffic. Honoured only where the flags
 *  service is keyless (local dev / an on-prem env that opted in via
 *  VITE_FLAGS_ON) — in any Amplitude-backed env the query param does nothing. */
export function previewLevel(search: string): PersonalizationLevel | undefined {
  if (FLAGS_ENABLED) return undefined;

  const raw = new URLSearchParams(search).get("plevel");
  return raw === "low" || raw === "medium" || raw === "high" ? raw : undefined;
}

/* Data-connected wrapper. Mounted only when the flag is on, so the extra read
 * it costs is never paid on the default (flag-off) path. */
export function ConnectedPersonalizationLevel({
  agentId,
}: {
  agentId: string;
}) {
  const { search } = useLocation();
  const { level } = usePersonalizationSignals(agentId);
  const shown = previewLevel(search) ?? level;

  return shown ? <PersonalizationLevelBar level={shown} /> : null;
}

export function PersonalizationLevelBar({
  level,
}: {
  level: PersonalizationLevel;
}) {
  const filled = levelIndex(level);

  // xl-only: the meter's whole point is reading against the Sessions card
  // beneath it, and below xl the KPI grid reflows to 2/1 columns so that
  // alignment can't hold. Width comes from the header's grid cell (see
  // AgentHeader) — don't pin one here or it stops tracking the cards.
  // w-max: the block is sized by its widest child — the nowrap caption — so the
  // bar ends exactly where the sentence ends, as in the design. Sizing it from
  // the KPI grid instead left the bar longer than the text.
  return (
    <div className="hidden w-max shrink-0 xl:block">
      <div className="flex items-center gap-2">
        <span
          className="text-[13px] leading-none"
          style={{ color: "var(--label)" }}
        >
          Personalization level
        </span>
        <MetricHint label="Personalization level" hint={PERSONALIZATION_HINT} />
        <span
          className="ml-auto text-[13px] leading-none"
          style={{ color: "var(--text-muted)" }}
        >
          {PERSONALIZATION_LABEL[level]}
        </span>
      </div>
      <div
        className="mt-2 flex gap-1.5"
        role="meter"
        aria-valuenow={filled}
        aria-valuemin={1}
        aria-valuemax={SEGMENTS}
        aria-valuetext={PERSONALIZATION_LABEL[level]}
        aria-label="Personalization level"
      >
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <span
            key={i}
            className="h-[3px] flex-1 rounded-full"
            style={{
              background: i < filled ? FILL[level] : "var(--toggle-off)",
            }}
          />
        ))}
      </div>
      {/* One line per level — the caption is sized to the bar, so the header's
          right block is what has to be wide enough (see AgentHeader). */}
      <p
        className="mt-2 whitespace-nowrap text-[13px] leading-snug"
        style={{ color: "var(--text-muted)" }}
      >
        {PERSONALIZATION_CAPTION[level]}
      </p>
    </div>
  );
}
